/**
 * Validierungs-Konfiguration für DataLayer
 *
 * Die Validierung wird aktiviert:
 * 1. AUTOMATISCH auf Integration (immer aktiv, nur echte Errors werden geloggt)
 * 2. localStorage: localStorage.setItem('debug_datalayer', 'true')
 * 3. URL Parameter: ?debug_datalayer=true
 *
 * Auf Integration-Umgebungen:
 * - Nur strukturelle Fehler werden mit console.error geloggt (für Sentry)
 * - Warnings (unknown parameters, etc.) werden NICHT geloggt
 * - Events werden immer gepusht, auch bei Fehlern
 */

/**
 * Prüft ob die aktuelle Umgebung Staging ist
 * @returns {boolean}
 */
export function isStaging() {
	if (typeof window === 'undefined' || !window.location) {
		return false;
	}

	const hostname = window.location.hostname;

	// Integration-Domains (Thalia/Merkur Staging)
	const stagingPatterns = [
		/^integration\./i,      // integration.thalia.de, integration.merkur.de
		/\.integration\./i,     // app.integration.thalia.de
		/-integration\./i       // app-integration.thalia.de
	];

	return stagingPatterns.some(pattern => pattern.test(hostname));
}

/**
 * Prüft ob Validierung über localStorage aktiviert ist
 * @returns {boolean}
 */
export function isValidationEnabledViaLocalStorage() {
	if (typeof window === 'undefined' || !window.localStorage) {
		return false;
	}

	try {
		return localStorage.getItem('debug_datalayer') === 'true';
	} catch (e) {
		// localStorage access might be blocked
		return false;
	}
}

/**
 * Prüft ob Validierung über URL-Parameter aktiviert ist
 * @returns {boolean}
 */
export function isValidationEnabledViaURL() {
	if (typeof window === 'undefined' || !window.location) {
		return false;
	}

	try {
		const urlParams = new URLSearchParams(window.location.search);
		return urlParams.get('debug_datalayer') === 'true';
	} catch (e) {
		return false;
	}
}

/**
 * Prüft ob Validierung aktiviert werden soll
 * @returns {boolean}
 */
export function isValidationEnabled() {
	// 1. Staging (Integration): IMMER aktiviert, damit Validierungsfehler an
	//    Sentry gemeldet werden. Production bleibt bewusst aus, um Sentry nicht
	//    mit Validierungsfehlern zu fluten.
	if (isStaging()) {
		return true;
	}

	// 2. Lokal nur über localStorage oder URL-Parameter
	return isValidationEnabledViaLocalStorage() || isValidationEnabledViaURL();
}

/**
 * Gibt die Validator-Optionen basierend auf der Umgebung zurück
 * @returns {Object}
 */
export function getValidatorOptions() {
	const debugEnabled = isValidationEnabledViaLocalStorage() || isValidationEnabledViaURL();

	return {
		strictMode: false,             // Strict mode deaktiviert (Warnings bleiben Warnings)
		validateCustomEvents: true,    // Custom Events immer validieren
		logToConsole: debugEnabled,    // Konsole nur beim gezielten Debugging (debug_datalayer) - kein Noise auf Staging/Prod
		useConsoleError: false,        // gruppierte Ausgabe beim Debugging
		reportToSentry: true           // Validierungsfehler immer an Sentry melden
	};
}
